import { Navigate } from 'react-router-dom';

import RequireModule from 'routes/components/RequireModule';

import { withLazyFallback } from 'lib/hoc/withLazyFallback';

import ChartRedirect from './ChartRedirect';

import {
  PRACTICE_CHARTS_CLINICAL_NOTES,
  PRACTICE_CHARTS_HEALTH_RECORDS,
  PRACTICE_CHARTS_ORDERS,
  PRACTICE_CHARTS_PAYMENTS
} from './index';

const NotFound = withLazyFallback(() => import('components/NotFound'));
const CMS1500 = withLazyFallback(
  () => import('components/practice/BeyondBilling/Claims/CMS1500/CMS1500')
);
const Appointments = withLazyFallback(
  () => import('components/practice/charts/Appointments/Appointments')
);
const PastAppointments = withLazyFallback(
  () => import('components/practice/charts/Appointments/PastAppointments')
);
const UpcomingAppointments = withLazyFallback(
  () => import('components/practice/charts/Appointments/UpcomingAppointments')
);
const CommunicationLogs = withLazyFallback(
  () => import('components/practice/charts/Communications/CommunicationLogs')
);
const CommunicationMessages = withLazyFallback(
  () => import('components/practice/charts/Communications/CommunicationMessages')
);
const Communications = withLazyFallback(
  () => import('components/practice/charts/Communications/Communications')
);
const Demographics = withLazyFallback(
  () => import('components/practice/charts/Demographics/Demographics')
);
const ChartDocuments = withLazyFallback(
  () => import('components/practice/charts/Documents/ChartDocuments')
);
const Overview = withLazyFallback(() => import('components/practice/charts/Overview/Overview'));
const PatientChart = withLazyFallback(
  () => import('components/practice/charts/PatientChart/PatientChart')
);
const PatientCharts = withLazyFallback(
  () => import('components/practice/charts/PatientCharts/PatientCharts')
);
const SuperBillPage = withLazyFallback(
  () => import('components/practice/charts/SuperBillPage/SuperBillPage')
);
const Billing = withLazyFallback(() => import('components/practice/charts/billing/Billing'));
const PatientClaims = withLazyFallback(
  () => import('components/practice/charts/billing/claims/PatientClaims')
);
const PatientCMS1500 = withLazyFallback(
  () => import('components/practice/charts/billing/PatientCMS1500/PatientCMS1500')
);
const Ledger = withLazyFallback(() => import('components/practice/charts/billing/ledger/Ledger'));
const BillingSuperbill = withLazyFallback(
  () => import('components/practice/charts/billing/superbill/Superbill')
);

// Children of the patient-chart :id route (Overview, Demographics, Appointments, etc.).
// Exported so the admin Practice Viewport can mount the same subtree under
// /admin/practices/:practiceId/charts/:id and reuse PatientChart end-to-end.
export const PATIENT_CHART_CHILDREN = [
  { path: '', element: <Overview />, handle: { title: 'Overview' } },
  { path: 'overview/*', element: <ChartRedirect to="" keepRest={false} /> },

  ...PRACTICE_CHARTS_CLINICAL_NOTES,
  ...PRACTICE_CHARTS_ORDERS,
  ...PRACTICE_CHARTS_PAYMENTS,

  {
    path: 'profile/:section?',
    element: <Demographics />,
    handle: { title: 'Profile' }
  },
  { path: 'demographics/*', element: <ChartRedirect to="profile" /> },

  {
    path: 'appointments/*',
    element: <Appointments />,
    handle: { title: 'Appointments' },
    children: [
      { index: true, element: <Navigate to="upcoming" replace /> },
      { path: 'upcoming', element: <UpcomingAppointments /> },
      { path: 'past', element: <PastAppointments /> }
    ]
  },

  ...PRACTICE_CHARTS_HEALTH_RECORDS,
  {
    path: 'billing/*',
    element: <Billing />,
    handle: { title: 'Billing' },
    children: [
      { path: 'ledger', element: <Ledger />, handle: { title: 'Ledger' } },
      {
        path: 'claims',
        element: (
          <RequireModule module="billing.claims">
            <PatientClaims />
          </RequireModule>
        ),
        handle: { title: 'Claims' },
        children: [{ path: ':claimId', element: <CMS1500 /> }]
      },
      {
        path: 'claim-details',
        element: (
          <RequireModule module="billing.claims">
            <PatientCMS1500 />
          </RequireModule>
        ),
        handle: { title: 'Claim details' }
      },
      { path: 'cms1500', element: <ChartRedirect to="billing/claim-details" keepRest={false} /> },
      {
        path: 'superbill',
        element: (
          <RequireModule module="billing.superbills">
            <BillingSuperbill />
          </RequireModule>
        ),
        handle: { title: 'Superbills' },
        children: [
          { path: ':superbill', element: <SuperBillPage /> },
          { path: 'claim/:claimId', element: <CMS1500 /> }
        ]
      },
      { path: 'invoices', element: <ChartRedirect to="payments/invoices" keepRest={false} /> },
      { path: 'plans/*', element: <ChartRedirect to="payments/plans" /> },
      {
        path: ':transactionId',
        element: <ChartRedirect to="payments/payments/:transactionId" keepRest={false} />
      },
      { path: '*', element: <NotFound /> }
    ]
  },

  { path: 'documents', element: <ChartDocuments />, handle: { title: 'Documents' } },

  { path: 'documents/:pathId', element: <ChartDocuments />, handle: { title: 'Documents' } },
  {
    path: 'communications',
    element: <Communications />,
    handle: { title: 'Communications' },
    children: [
      { path: '', element: <CommunicationMessages /> },
      { path: 'logs', element: <CommunicationLogs />, handle: { title: 'Notification logs' } },
      { path: '*', element: <NotFound /> }
    ]
  },

  { path: '*', element: <NotFound /> }
];

const PRACTICE_CHARTS = [
  {
    path: 'charts',
    element: <PatientCharts />,
    handle: { title: 'Charts' },
    children: [
      {
        path: ':id',
        element: <PatientChart />,
        children: PATIENT_CHART_CHILDREN
      },
      { path: '*', element: <NotFound /> }
    ]
  }
];

export default PRACTICE_CHARTS;
